import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { useAuth } from './useAuth';

export default function LoginPage() {
  const { t } = useTranslation();
  const { session, signIn } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (session) {
    // Return the user to where they were headed, but never to a page that only
    // exists to say "no" — signing in again and landing back on /forbidden reads
    // like the sign-in failed.
    const from = (location.state as { from?: string } | null)?.from;
    const skip = ['/login', '/forbidden', '/update-password'];
    return <Navigate to={from && !skip.includes(from) ? from : '/'} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch {
      // Deliberately one message for both "no such account" and "wrong password":
      // distinguishing them tells an attacker which addresses are real.
      setError(t('auth.invalidCredentials'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-lg font-bold text-slate-900">{t('app.name')}</h1>
          <LanguageToggle />
        </div>

        <form
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="text-base font-semibold text-slate-900">{t('auth.signInTitle')}</h2>

          {error ? <Alert tone="error">{error}</Alert> : null}

          <Input
            label={t('auth.email')}
            type="email"
            name="email"
            autoComplete="username"
            required
            dir="ltr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Input
            label={t('auth.password')}
            type="password"
            name="password"
            autoComplete="current-password"
            required
            dir="ltr"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button type="submit" loading={submitting}>
            {t('auth.signIn')}
          </Button>

          <Link
            to="/forgot-password"
            className="text-center text-xs text-brand-700 hover:underline"
          >
            {t('auth.forgotPassword')}
          </Link>
        </form>

        <p className="mt-4 text-center text-xs text-slate-500">{t('auth.noSelfSignup')}</p>
      </div>
    </main>
  );
}
